import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Popover, Tooltip } from "antd";
import type { Dayjs } from "dayjs";
import {
  HiOutlinePlus,
  HiOutlinePencilSquare,
  HiOutlineDocumentDuplicate,
  HiOutlineTrash,
  HiOutlineCheckCircle,
  HiOutlineChatBubbleLeftRight,
  HiOutlinePaperAirplane,
  HiOutlineChartBarSquare,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { Panel, FormButton, DateRangePicker, FormSelect } from "../../ui";
import "../../ui/simple-table";
import { promptsApi } from "../../api";
import type { IPromptsStatsResponse, ITemplateStats } from "../../api/prompts";
import { IPrompt, IPromptPayload, ITeamMemberRecord } from "../../data-access";
import { notify, capitalize } from "../../util";
import { RootState } from "../../store";
import { PromptEditor } from "../ai-prompts/prompt-editor";
import "./ai-templates.scss";

type EditorMode = "create" | "edit" | "duplicate";

// ── Template Data types ──
interface ITemplateDataItem {
  id: string;
  name: string;
  value: string;
}

const MAX_DATA_ITEMS = 10;
const DATA_NAME_MAX = 100;
const DATA_VALUE_MAX = 5000;
const genId = () => Math.random().toString(36).slice(2, 9);

const PERIOD_FILTERS = [
  { value: "current_week", label: "Current Week" },
  { value: "last_week", label: "Last Week" },
  { value: "current_month", label: "Current Month" },
  { value: "last_month", label: "Last Month" },
];


// ── Rate color helper ──
const rateClass = (rate: number, used: number): string => {
  if (used === 0) return "";
  if (rate >= 15) return "tpl-rate--great";
  if (rate >= 10) return "tpl-rate--good";
  if (rate >= 5) return "tpl-rate--ok";
  return "tpl-rate--low";
};

export const AITemplates = (): JSX.Element => {
  const teamMembers = useSelector((state: RootState) => state.team.members);
  const [templates, setTemplates] = useState<IPrompt[]>([]);
  const [defaultId, setDefaultId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState("current_week");
  const [statsRangeDate, setStatsRangeDate] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [userFilter, setUserFilter] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [perTemplateStats, setPerTemplateStats] = useState<Record<string, ITemplateStats>>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set([0]));

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  const [totalStats, setTotalStats] = useState<IPromptsStatsResponse["total"]>({ proposals: 0, bids: 0, replies: 0, reply_rate: 0, offers: 0 });

  // Template Data (tabs) — loaded from backend
  const [dataItems, setDataItems] = useState<ITemplateDataItem[]>([]);
  const [activeDataTab, setActiveDataTab] = useState<string>("");
  const [dataSaveStatus, setDataSaveStatus] = useState<"" | "saving" | "saved">("");
  const [dataLoading, setDataLoading] = useState(true);

  // Editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>("create");
  const [editingTemplate, setEditingTemplate] = useState<IPrompt | null>(null);
  const [editorName, setEditorName] = useState("");
  const [editorPromptText, setEditorPromptText] = useState("");

  // Save template data to backend (called on blur)
  const saveTemplateData = async (items: ITemplateDataItem[], successMessage = "Template data saved") => {
    setDataSaveStatus("saving");
    try {
      const validItems = items.filter((i) => i.name.trim());
      if (validItems.length > 0) {
        const resp = await promptsApi.syncTemplateData(
          validItems.map((i) => ({ name: i.name, value: i.value }))
        );
        // Update IDs from backend
        setDataItems((prev) => {
          const updated = prev.map((item, idx) => {
            const serverItem = resp.data[idx];
            return serverItem ? { ...item, id: String(serverItem.id) } : item;
          });
          setActiveDataTab((prevTab) => {
            const oldIdx = prev.findIndex((i) => i.id === prevTab);
            return oldIdx >= 0 && updated[oldIdx] ? updated[oldIdx].id : prevTab;
          });
          return updated;
        });
      }
      setDataSaveStatus("saved");
      notify("success", successMessage);
    } catch {
      setDataSaveStatus("");
      notify("error", "Failed to save template data");
    }
    setTimeout(() => setDataSaveStatus(""), 2000);
  };
  const handleDataChange = (fn: (prev: ITemplateDataItem[]) => ITemplateDataItem[]) => {
    setDataItems(fn);
  };
  const handleAddDataItem = () => {
    if (dataItems.length >= MAX_DATA_ITEMS) { notify("error", "Maximum 10 items"); return; }
    const newItem: ITemplateDataItem = { id: genId(), name: "", value: "" };
    handleDataChange((prev) => [...prev, newItem]);
    setActiveDataTab(newItem.id);
  };
  const handleDeleteDataItem = (id: string) => {
    const updated = dataItems.filter((item) => item.id !== id);
    setDataItems(updated);
    saveTemplateData(updated, "Template data removed");
    if (activeDataTab === id) {
      setActiveDataTab(updated[0]?.id || "");
    }
  };

  // Delete + menu
  const [deleteTarget, setDeleteTarget] = useState<IPrompt | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [dataDeleteTarget, setDataDeleteTarget] = useState<ITemplateDataItem | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await promptsApi.getPrompts();
      setTemplates(response.data);
      const defaultPrompt = response.data.find((p) => p.default);
      if (defaultPrompt) {
        setDefaultId(defaultPrompt.id);
      } else if (response.data.length > 0) {
        setDefaultId(response.data[0].id);
      }
    } catch {
      notify("error", "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplateData = useCallback(async () => {
    try {
      const resp = await promptsApi.getTemplateData();
      const items = resp.data.map((d) => ({
        id: String(d.id),
        name: d.name,
        value: d.value,
      }));
      setDataItems(items);
      if (items.length > 0) setActiveDataTab(String(items[0].id));
    } catch {
      notify("error", "Failed to load template data");
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); fetchTemplateData(); }, [fetchTemplates, fetchTemplateData]);

  // Fetch stats when period, user, or date range changes
  useEffect(() => {
    let params = "";
    if (statsRangeDate[0] && statsRangeDate[1]) {
      params = `date_from=${statsRangeDate[0].format("YYYY-MM-DD")}&date_to=${statsRangeDate[1].format("YYYY-MM-DD")}`;
    } else if (statsPeriod) {
      params = `time_period=${statsPeriod}`;
    }
    if (userFilter) {
      params += `${params ? "&" : ""}user_id=${userFilter}`;
    }

    promptsApi
      .getPromptsStats(params)
      .then((response) => {
        setPerTemplateStats(response.per_template || {});
        setTotalStats(response.total || { proposals: 0, bids: 0, replies: 0, reply_rate: 0, offers: 0 });
      })
      .catch(() => {
        setPerTemplateStats({});
        setTotalStats({ proposals: 0, bids: 0, replies: 0, reply_rate: 0, offers: 0 });
      });
  }, [statsPeriod, userFilter, statsRangeDate]);

  // ── Editor handlers ──
  const openCreate = () => { setEditorMode("create"); setEditingTemplate(null); setEditorName(""); setEditorPromptText(""); setEditorOpen(true); };
  const openEdit = (t: IPrompt) => { setEditorMode("edit"); setEditingTemplate(t); setEditorName(t.name); setEditorPromptText(t.prompt); setEditorOpen(true); };
  const openDuplicate = (t: IPrompt) => { setEditorMode("duplicate"); setEditingTemplate(null); setEditorName(`${t.name} (Copy)`); setEditorPromptText(t.prompt); setEditorOpen(true); };

  const handleSave = async () => {
    const payload: IPromptPayload = { name: editorName.trim(), prompt: editorPromptText };
    setSaving(true);
    try {
      if (editorMode === "edit" && editingTemplate) {
        const response = await promptsApi.updatePrompt(editingTemplate.id, payload);
        setTemplates((prev) => prev.map((t) => (t.id === editingTemplate.id ? response.data : t)));
        notify("success", "Template updated");
      } else {
        const response = await promptsApi.createPrompt(payload);
        setTemplates((prev) => [...prev, response.data]);
        setEditingTemplate(response.data);
        setEditorMode("edit");
        notify("success", "Template created");
      }
    } catch { notify("error", "Failed to save template"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await promptsApi.deletePrompt(deleteTarget.id);
      setTemplates((prev) => {
        const remaining = prev.filter((t) => t.id !== deleteTarget.id);
        if (deleteTarget.id === defaultId && remaining.length > 0) {
          setDefaultId(remaining[0].id);
        }
        return remaining;
      });
      notify("success", "Template deleted");
    } catch { notify("error", "Failed to delete template"); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  // ── Derived ──
  const editorTitle = editorMode === "edit" ? "Edit Template" : editorMode === "duplicate" ? "Duplicate Template" : "New Template";
  const editorSaveLabel = editorMode === "edit" ? "Save Changes" : "Create Template";

  const otherTemplates = templates;

  // Per-template stats helper
  const getTemplateStats = (t: IPrompt): ITemplateStats => perTemplateStats[String(t.id)] || { sent: 0, bids: 0, replies: 0, reply_rate: 0, offers: 0 };

  // ── User picker ──
  const getInitials = (member: ITeamMemberRecord): string => {
    const name = member.full_name || member.email;
    return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  };

  const selectedUser = teamMembers.find((m) => String(m.id) === userFilter);

  const userPicker = (
    <Popover
      content={
        <div className="tpl-user-picker__list">
          <div className={`tpl-user-picker__item ${!userFilter ? "active" : ""}`} onClick={() => { setUserFilter(""); setUserPickerOpen(false); }}>
            <span className="tpl-user-picker__avatar tpl-user-picker__avatar--all"><HiOutlineUserGroup size={16} /></span>
            <span>All Members</span>
          </div>
          {teamMembers.map((member) => (
            <div key={member.id} className={`tpl-user-picker__item ${userFilter === String(member.id) ? "active" : ""}`} onClick={() => { setUserFilter(String(member.id)); setUserPickerOpen(false); }}>
              <span className="tpl-user-picker__avatar">{getInitials(member)}</span>
              <span>{member.full_name ?? capitalize(member.email.substring(0, member.email.indexOf("@")))}</span>
            </div>
          ))}
        </div>
      }
      trigger="click"
      open={userPickerOpen}
      onOpenChange={setUserPickerOpen}
      placement="bottomRight"
      overlayClassName="tpl-user-picker__popover"
    >
      <div className="tpl-user-picker__trigger">
        {selectedUser ? (
          <span className="tpl-user-picker__avatar">{getInitials(selectedUser)}</span>
        ) : (
          <span className="tpl-user-picker__avatar tpl-user-picker__avatar--all"><HiOutlineUserGroup size={16} /></span>
        )}
      </div>
    </Popover>
  );

  // ── Shimmer ──
  if (loading) return (
    <Panel className="ai-templates" title="Cover Letters">
      {/* Stats shimmer */}
      <div className="tpl-stats">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="tpl-stats__item">
            <div className="tpl-shimmer tpl-shimmer--stat-value" />
            <div className="tpl-shimmer tpl-shimmer--stat-label" />
          </div>
        ))}
      </div>
      <div className="tpl-divider" />
      {/* Section title shimmer */}
      <div className="tpl-actions-row">
        <div className="tpl-shimmer tpl-shimmer--section-title" />
        <div className="tpl-shimmer tpl-shimmer--btn" />
      </div>
      {/* Table shimmer */}
      <div className="simple-table tpl-table">
        <div className="simple-table__header tpl-table__grid">
          <span className="simple-table__col">Name</span>
          <span className="simple-table__col">Sent</span>
          <span className="simple-table__col">Replies</span>
          <span className="simple-table__col">Reply Rate</span>
          <span className="simple-table__col">Offers</span>
          <span className="simple-table__col simple-table__col--right">Action</span>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="simple-table__row tpl-table__grid">
            <div className="simple-table__col">
              <div className="tpl-table__name-cell">
                <div className="tpl-shimmer tpl-shimmer--row-name" />
                <div className="tpl-shimmer tpl-shimmer--row-preview" />
              </div>
            </div>
            <span className="simple-table__col"><div className="tpl-shimmer tpl-shimmer--row-stat" /></span>
            <span className="simple-table__col"><div className="tpl-shimmer tpl-shimmer--row-stat" /></span>
            <span className="simple-table__col"><div className="tpl-shimmer tpl-shimmer--row-stat" /></span>
            <span className="simple-table__col"><div className="tpl-shimmer tpl-shimmer--row-stat" /></span>
            <span className="simple-table__col simple-table__col--right"><div className="tpl-shimmer tpl-shimmer--row-action" /></span>
          </div>
        ))}
      </div>
    </Panel>
  );

  // ── Empty ──
  if (templates.length === 0) return (
    <Panel className="ai-templates" title="Cover Letters">
      <div className="tpl-empty">
        <HiOutlineDocumentDuplicate size={48} />
        <h3>No cover letters yet</h3>
        <p>Create your first cover letter to start generating personalized proposals</p>
        <FormButton theme="green-light" icon={<HiOutlinePlus />} onClick={openCreate}>Create Template</FormButton>
      </div>
    </Panel>
  );

  return (
    <Panel
      className="ai-templates"
      title="Cover Letters"
      nodeInTitleRow={
        <div className="tpl-title-filters">
          {userPicker}
          <ul className="tpl-period-tabs">
            {PERIOD_FILTERS.map((p) => (
              <li key={p.value} className={statsPeriod === p.value ? "active" : ""} onClick={() => { setStatsPeriod(p.value); setStatsRangeDate([null, null]); }}>
                {p.label}
              </li>
            ))}
          </ul>
          <div className="tpl-period-select">
            <FormSelect
              options={PERIOD_FILTERS}
              value={statsPeriod || undefined}
              placeholder="Select period"
              onChange={(val) => { setStatsPeriod(val as string); setStatsRangeDate([null, null]); }}
            />
          </div>
          <DateRangePicker onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              setStatsRangeDate(dates as [Dayjs, Dayjs]);
              setStatsPeriod("");
            } else {
              setStatsRangeDate([null, null]);
              setStatsPeriod("current_week");
            }
          }} />
        </div>
      }
    >
      {/* ── Summary stats ── */}
      <div className="tpl-stats">
        <div className="tpl-stats__item">
          <span className="tpl-stats__value">{templates.length}</span>
          <span className="tpl-stats__label"><HiOutlineDocumentDuplicate size={14} /> Templates</span>
        </div>
        <div className="tpl-stats__item">
          <span className="tpl-stats__value">{totalStats.bids}</span>
          <span className="tpl-stats__label"><HiOutlinePaperAirplane size={14} /> Proposals Sent</span>
        </div>
        <div className="tpl-stats__item">
          <span className="tpl-stats__value">{totalStats.replies}</span>
          <span className="tpl-stats__label"><HiOutlineChatBubbleLeftRight size={14} /> Replies</span>
        </div>
        <div className="tpl-stats__item">
          <span className={`tpl-stats__value ${rateClass(totalStats.reply_rate, totalStats.bids)}`}>{totalStats.reply_rate}<span className="tpl-stats__unit">%</span></span>
          <span className="tpl-stats__label"><HiOutlineChartBarSquare size={14} /> Avg Reply Rate</span>
        </div>
      </div>

      {/* ── All templates table ── */}
      {otherTemplates.length > 0 && (
        <>
          <div className="tpl-divider" />
          <div className="tpl-actions-row">
            <div className="tpl-section-label">All Templates</div>
            <FormButton theme="green-light" icon={<HiOutlinePlus />} onClick={openCreate}>
              New
            </FormButton>
          </div>
          <div className="simple-table tpl-table">
            <div className="simple-table__header tpl-table__grid">
              <span className="simple-table__col">Name</span>
              <span className="simple-table__col">Sent</span>
              <span className="simple-table__col">Replies</span>
              <span className="simple-table__col">Reply Rate</span>
              <span className="simple-table__col">Offers</span>
              <span className="simple-table__col simple-table__col--right">Action</span>
            </div>
            {otherTemplates.map((t, index) => {
              const ts = getTemplateStats(t);
              return (
                <div
                  key={t.id}
                  className={`simple-table__row tpl-table__grid${expandedRows.has(index) ? " simple-table__row--expanded" : ""}`}
                  onClick={(e) => {
                    const header = e.currentTarget.firstElementChild;
                    if (header && header.contains(e.target as Node)) toggleRow(index);
                  }}
                >
                  <div className="simple-table__col simple-table__col--member" data-label="">
                    <div className="tpl-table__name-cell">
                      <div className="tpl-table__name-row">
                        <span className="tpl-table__name">{t.name}</span>
                        {t.id === defaultId && (
                          <span className="tpl-table__default-badge"><HiOutlineCheckCircle size={11} /> Default</span>
                        )}
                      </div>
                      <span className="tpl-table__preview">{t.prompt.slice(0, 80)}...</span>
                    </div>
                  </div>
                  <span className="simple-table__col simple-table__col--primary" data-label="Sent">{ts.bids}</span>
                  <span className="simple-table__col simple-table__col--primary" data-label="Replies">{ts.replies}</span>
                  <span className={`simple-table__col simple-table__col--primary ${rateClass(ts.reply_rate, ts.bids)}`} data-label="Reply Rate">{ts.reply_rate}<span className="tpl-table__pct">%</span></span>
                  <span className="simple-table__col simple-table__col--primary" data-label="Offers">{ts.offers}</span>
                  <span className="simple-table__col simple-table__col--right" data-label="Action">
                    <div className="tpl-row__actions">
                      {t.id !== defaultId && (
                        <Tooltip title="Set as default template" placement="top">
                          <button className="tpl-action-btn" onClick={() => { setDefaultId(t.id); promptsApi.setDefaultPrompt(t.id).catch(() => {}); notify("success", `"${t.name}" set as default`); }}>
                            <HiOutlineCheckCircle size={16} />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit template" placement="top">
                        <button className="tpl-action-btn" onClick={() => openEdit(t)}>
                          <HiOutlinePencilSquare size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Duplicate template" placement="top">
                        <button className="tpl-action-btn" onClick={() => openDuplicate(t)}>
                          <HiOutlineDocumentDuplicate size={16} />
                        </button>
                      </Tooltip>
                      {t.id !== defaultId && (
                        <Tooltip title="Delete template" placement="top">
                          <button className="tpl-action-btn tpl-action-btn--delete" onClick={() => setDeleteTarget(t)}>
                            <HiOutlineTrash size={16} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Template Data ── */}
      <div className="tpl-divider" />
      <div className="tpl-actions-row">
        <div className="tpl-section-label">Template Data {dataSaveStatus && <span className={`tpl-save-status tpl-save-status--${dataSaveStatus}`}>{dataSaveStatus === "saving" ? "Saving..." : "Saved"}</span>}</div>
      </div>
      <p className="tpl-data-desc">Store your portfolio, signature, rates and other details here. AI will use this data when generating cover letters from your templates.</p>

      <div className="tpl-data">
        {dataLoading ? (
          <div className="tpl-data__shimmer">
            <div className="tpl-data__tabs">
              <div className="tpl-shimmer-tab" /><div className="tpl-shimmer-tab" />
            </div>
            <div className="tpl-data__panel">
              <div className="tpl-shimmer-field" />
              <div className="tpl-shimmer-textarea" />
            </div>
          </div>
        ) : (<>
        <div className="tpl-data__tabs">
          {dataItems.map((item) => (
            <button
              key={item.id}
              className={`tpl-data__tab ${activeDataTab === item.id ? "active" : ""}`}
              onClick={() => setActiveDataTab(item.id)}
            >
              {item.name || "Unnamed"}
            </button>
          ))}
          {dataItems.length < MAX_DATA_ITEMS && (
            <button className="tpl-data__tab tpl-data__tab--add" onClick={handleAddDataItem}>
              <HiOutlinePlus size={13} />
            </button>
          )}
        </div>

        {dataItems.map((item, index) => item.id === activeDataTab && (
          <div key={item.id} className="tpl-data__panel">
            <div className="tpl-data__field">
              <label className="tpl-data__field-label">Name</label>
              <div className="tpl-data__input-wrap">
                <input
                  className="tpl-data__input"
                  placeholder="e.g. Portfolio, Signature, About Me"
                  value={item.name}
                  maxLength={DATA_NAME_MAX}
                  onChange={(e) => handleDataChange((prev) => prev.map((d) => d.id === item.id ? { ...d, name: e.target.value } : d))}
                  onBlur={() => saveTemplateData(dataItems)}
                />
                <span className={`tpl-data__counter ${item.name.length > DATA_NAME_MAX ? "tpl-data__counter--error" : ""}`}>
                  {item.name.length}/{DATA_NAME_MAX}
                </span>
              </div>
            </div>
            <div className="tpl-data__field">
              <label className="tpl-data__field-label">Value</label>
              <div className="tpl-data__input-wrap">
                <textarea
                  className="tpl-data__input tpl-data__input--textarea"
                  placeholder="Enter the content that will be inserted into your cover letter..."
                  rows={5}
                  value={item.value}
                  maxLength={DATA_VALUE_MAX}
                  onChange={(e) => handleDataChange((prev) => prev.map((d) => d.id === item.id ? { ...d, value: e.target.value } : d))}
                  onBlur={() => saveTemplateData(dataItems)}
                />
                <span className={`tpl-data__counter ${item.value.length > DATA_VALUE_MAX ? "tpl-data__counter--error" : ""}`}>
                  {item.value.length}/{DATA_VALUE_MAX}
                </span>
              </div>
            </div>
            <div className="tpl-data__panel-footer">
              {item.name && <span className="tpl-data__tag">Use <code>[{item.name}]</code> in your template to insert this value</span>}
              {/* Portfolio & Signature are protected defaults — double check:
                  position AND name must both match (slot 0 = Portfolio,
                  slot 1 = Signature). A user-created item that merely happens
                  to be named "Portfolio" stays removable. */}
              {!(
                (index === 0 && item.name.trim().toLowerCase() === "portfolio") ||
                (index === 1 && item.name.trim().toLowerCase() === "signature")
              ) && (
                <button className="tpl-data__del" onClick={() => setDataDeleteTarget(item)}><HiOutlineTrash size={13} /> Remove</button>
              )}
            </div>
          </div>
        ))}
        </>)}
      </div>

      {/* ── Editor modal ── */}
      <PromptEditor
        isOpen={editorOpen}
        title={editorTitle}
        name={editorName}
        prompt={editorPromptText}
        saving={saving}
        saveLabel={editorSaveLabel}
        variables={dataItems.filter((d) => d.name).map((d) => ({ label: d.name, value: `[${d.name}]`, content: d.value || "" }))}
        onNameChange={setEditorName}
        onPromptChange={setEditorPromptText}
        onSave={handleSave}
        onCancel={() => setEditorOpen(false)}
      />

      {/* ── Delete dialog ── */}
      {deleteTarget && (
        <div className="tpl-delete-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="tpl-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Template</h3>
            <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            <div className="tpl-delete-actions">
              <FormButton theme="grey" onClick={() => setDeleteTarget(null)}>Cancel</FormButton>
              <FormButton theme="red" onClick={handleDelete} loading={deleting}>Delete</FormButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete template data dialog ── */}
      {dataDeleteTarget && (
        <div className="tpl-delete-overlay" onClick={() => setDataDeleteTarget(null)}>
          <div className="tpl-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Remove Template Data</h3>
            <p>Are you sure you want to remove <strong>{dataDeleteTarget.name || "this item"}</strong>? This cannot be undone.</p>
            <div className="tpl-delete-actions">
              <FormButton theme="grey" onClick={() => setDataDeleteTarget(null)}>Cancel</FormButton>
              <FormButton
                theme="red"
                onClick={() => { handleDeleteDataItem(dataDeleteTarget.id); setDataDeleteTarget(null); }}
              >
                Remove
              </FormButton>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};

export default AITemplates;
